// Per-integration permission helpers.
//
// The team-permissions dialog stores granted sidebar groups in a flat string
// array on `users.preferences.menu_permissions`. Per-integration permissions
// piggy-back on the same array using the prefix `integration:<key>` (e.g.
// `integration:payu`) — this keeps the storage shape unchanged so no
// migration / new column is needed.
//
// Resolution rules (in order):
//   1. menu_permissions === null/undefined → full access (legacy default).
//   2. menu_permissions has NO `integration:*` keys → all integrations
//      allowed (legacy mode — preserves existing custom-permission users
//      who haven't been migrated to the per-integration model yet).
//   3. menu_permissions has at least one `integration:*` key → only the
//      explicitly listed integrations are allowed.
//
// NOTE: there used to be a rule requiring the parent `settings` group to be
// present in the array before any integration was granted. That rule was
// removed (2026-07-23) because MemberPermissionsDialog structurally can
// never write `'settings'` into the saved array (it's excluded from the
// togglable list — Account Settings is always shown regardless of custom
// permissions, see AppSidebar.tsx's `isPrimaryGroupAllowed`). The old rule
// meant EVERY user who ever had custom permissions saved for them — even
// with "full access" / all boxes checked — permanently lost all integration
// access with no way to restore it short of clearing back to null. Do not
// reintroduce a `settings`-presence gate here without also making the
// dialog able to grant it.

export const INTEGRATION_PERMISSION_PREFIX = 'integration:';

export const integrationPermissionId = (entitlementKey: string): string =>
  `${INTEGRATION_PERMISSION_PREFIX}${entitlementKey}`;

export const isIntegrationPermissionId = (id: string): boolean =>
  id.startsWith(INTEGRATION_PERMISSION_PREFIX);

export const entitlementKeyFromPermissionId = (id: string): string | null =>
  isIntegrationPermissionId(id) ? id.slice(INTEGRATION_PERMISSION_PREFIX.length) : null;

export interface IntegrationPermissionInput {
  menu_permissions: string[] | null | undefined;
  entitlementKey: string;
}

export const isIntegrationGrantedByPermissions = ({
  menu_permissions,
  entitlementKey,
}: IntegrationPermissionInput): boolean => {
  if (!Array.isArray(menu_permissions)) return true;
  const hasExplicitIntegrationKeys = menu_permissions.some(isIntegrationPermissionId);
  if (!hasExplicitIntegrationKeys) return true;
  return menu_permissions.includes(integrationPermissionId(entitlementKey));
};
